<template>
    <button
        :id="`select-option-${uuid}`"
        :aria-disabled="disabled"
        :class="[
            'relative block flex items-center w-full text-left select-none relative py-2 border-gray-200 scroll-mt-10',
            'transition duration-150',
            'focus:outline-none',
            state.multiple ? 'px-3' : 'pl-3 pr-9',
            isSingleSelected ? 'font-semibold text-blue-500' : '',
            disabled ? 'text-gray-400 cursor-not-allowed italic' : 'hover:bg-gray-100 focus:bg-gray-150',
        ]"
        :data-option="true"
        :data-selected="isSelected"
        data-test="select-option"
        :data-value="value"
        type="button"
        @click="onSelect"
        @keydown.enter.prevent.stop="onSelect"
        @keydown.space.prevent.stop="onSelect"
    >
        <ActivixCheckbox
            class="mr-3"
            data-test="select-option-checkbox"
            :disabled="disabled"
            size="sm"
            :value="isSelected"
            @input="onSelect"
            v-if="state.multiple && showCheckbox"
        />
        <div class="inline-flex mr-2" data-test="select-option-icon" v-if="$slots.icon">
            <slot name="icon" />
        </div>
        <slot :is-selected="isSelected">
            <span
                class="block truncate"
                :class="colorClassName"
                data-test="select-option-label"
                :title="label"
                v-text="label"
            />
        </slot>
        <span
            class="ml-2 flex items-center justify-center"
            data-test="select-option-tooltip"
            v-tooltip="tooltip"
            v-if="tooltip"
        >
            <InformationCircle class="h-4 w-4 text-gray-500" />
        </span>
        <span class="absolute inset-y-0 right-0 flex items-center justify-center w-9 pointer-events-none" data-test="select-option-checkmark" v-if="isSingleSelected && showCheckmark">
            <InterfaceValidationCheck :class="[colorClassName || 'text-blue-500', $ui.oldFontScale ? 'h-3.5 w-3.5' : 'h-3 w-3']" />
        </span>
    </button>
</template>

<script>
    import { v4 as uuidv4 } from 'uuid';
    import InterfaceValidationCheck from '../../assets/svg/bold/check.svg';
    import InformationCircle from '../../assets/svg/line/information-circle.svg';
    import ActivixCheckbox from '../Checkbox/Checkbox.vue';

    /**
     * @displayName Select Option
     * @example [none]
     */
    export default {
        name: 'ActivixSelectOption',

        components: { ActivixCheckbox, InterfaceValidationCheck, InformationCircle },

        inject: ['state', 'events'],

        props: {
            label: {
                type: String,
                default: '',
            },
            value: {
                type: null,
                required: true,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            tooltip: {
                type: String,
                default: '',
            },
            showCheckmark: {
                type: Boolean,
                default: true,
            },
            showCheckbox: {
                type: Boolean,
                default: true,
            },
            colorClassName: {
                type: String,
                default: '',
            },
        },

        computed: {
            isSelected() {
                return this.state.selectedOptions.some(selectedOption => {
                    return selectedOption.value === this.value;
                });
            },

            isSingleSelected() {
                return this.isSelected && !this.state.multiple;
            },
        },

        methods: {
            onSelect(event) {
                if (this.disabled) {
                    return;
                }

                const isEnter = event && (event.code === 'Enter' || event.key === 'Enter');

                this.events.onSelect(this.value, this.$el, isEnter);
            },
        },

        beforeCreate() {
            this.uuid = uuidv4();
        },
    };
</script>
